import {
  AdminSubHeaderMainEditLayout,
  AdminSubHeaderMainEditLayoutV3_1,
  PageCard,
  PageCardSection,
} from 'Components/Display';
import { useEntity, useGlobalContext } from 'Context';
import { addEditRoute, AddEditSubRoute, ROUTES } from 'Navigation';
import React, { Fragment, useEffect } from 'react';
import AccessTime from '@mui/icons-material/AccessTime';
import {
  faUserMd,
  faBusinessTime,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import { ViewReferralBreadcrumbs } from '.';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Row } from 'reactstrap';
import { dayjs } from 'Utilities/DateTimeUtc';
import { useHistory, useParams } from 'react-router-dom';
import { PrimaryButton } from 'Components/Form';
import { parseRawDateString, parseUtcToLocal } from 'Utilities';
import { makeStyles } from '@mui/styles';
import { ProgressBar } from 'react-bootstrap';
import { SecureComponent } from 'Components/Security';
import { Permission } from 'Models/Templates/Permission/Permission';
import { ReferralOwnershipModal, useOwnershipModal } from './ReferralOwnershipModal';
import { ReferralOwnershipBanner } from './ReferralOwnershipBanner';

export const ReferralMain: React.FC = () => {
  const entity = useEntity();
  const history = useHistory();
  const { id: urlId } = useParams<{ id: string }>();
  const redirectToAddedit = () => {
    history.push(
      addEditRoute(AddEditSubRoute.Appointment, entity.appointment.id)
    );
  };
  const userdata = useGlobalContext()[0]?.currentUser;

  // Get the referral ID from either URL params or the entity
  const id = urlId || entity?.referralId || entity?.id;

  const { isInternalUser, showModal, showBanner, bannerInfo, handleOwnershipClaimed, handleModalDismiss, handleDeny } =
    useOwnershipModal(entity, userdata, id);

  const useStyles = makeStyles({
    progressBar: {
      width: '100%',
      height: 25,
      fontSize: '.9rem',
      marginBottom: '1rem',
    },
    emptyChunk: {
      background: '#e9ecef',
      color: '#b2b1b1',
      borderRightStyle: 'solid',
      borderRightColor: 'white',
      borderRightWidth: 2,
    },
    filledChunk: {
      background: '#28a745',
      borderRightStyle: 'solid',
      borderRightColor: 'white',
      borderRightWidth: 2,
    },
    lastEmptyChunk: {
      background: '#e9ecef',
      color: '#b2b1b1',
    },
    lastFilledChunk: {
      background: '#28a745',
    },
  });

  const classes = useStyles();

  //Value represents "percentage" in ProgressBar
  const [value, setValue] = React.useState(0);

  //Assign Appointment Status value
  let appointmentStatusValue =
    entity.appointment != undefined &&
    entity?.appointment?.appointmentStatus != null
      ? entity?.appointment?.appointmentStatus?.value
      : null;

  //setValue according current Appointment Status
  useEffect(() => {
    switch (appointmentStatusValue) {
      case 1: {
        setValue(25);
        break;
      }
      case 2: {
        setValue(50);
        break;
      }
      case 3: {
        setValue(75);
        break;
      }
      case 4: {
        setValue(50);
        break;
      }
      case 5: {
        setValue(100);
        break;
      }
      case 6: {
        setValue(100);
        break;
      }
      default: {
        break;
      }
    }
  }, [appointmentStatusValue]);

  //ProgressBar JSX Element
  let appointmentStatusBar = (
    <Fragment>
      <ProgressBar className={classes.progressBar}>
        <ProgressBar
          className={value > 0 ? classes.filledChunk : classes.emptyChunk}
          now={25}
          label="Open"
        />
        <ProgressBar
          className={value > 25 ? classes.filledChunk : classes.emptyChunk}
          now={25}
          label="Assigned/Under-Review"
        />
        <ProgressBar
          className={value > 50 ? classes.filledChunk : classes.emptyChunk}
          now={25}
          label="In QA"
        />
        <ProgressBar
          className={
            value > 75 ? classes.lastFilledChunk : classes.lastEmptyChunk
          }
          now={25}
          label="Closed"
        />
      </ProgressBar>
    </Fragment>
  );

  let appointmentDateBlock;
  let referralDueDateBlock;
  let reportDueDateBlock;

  if (entity.appointment?.reportDueDate) {
    reportDueDateBlock = (
      <Fragment>
        <Col className="" xs="12" sm="6" lg="2">
          <table className="text-left">
            <tbody>
              <tr>
                <td>
                  <AccessTime className="color-secondary mr-2 header-size-regular " />
                </td>
                <td>
                  <b>Report Due Date</b>
                  <br />
                  {parseUtcToLocal(entity.appointment?.reportDueDate)
                    ? dayjs(parseUtcToLocal(entity.appointment?.reportDueDate)).format(
                        'MM/DD/YYYY hh:mm A'
                      )
                    : ''}
                </td>
              </tr>
            </tbody>
          </table>
        </Col>
      </Fragment>
    );
  }

  if (entity.appointment?.appointmentDate) {
    appointmentDateBlock = (
      <Fragment>
        <Col className="" xs="12" sm="6" lg="2">
          <table className="text-left">
            <tbody>
              <tr>
                <td>
                  <AccessTime className="color-secondary mr-2 header-size-regular " />
                </td>
                <td>
                  <b>Appointment Date</b>
                  <br />
                  {parseUtcToLocal(entity.appointment?.appointmentDate)
                    ? dayjs(parseUtcToLocal(entity.appointment?.appointmentDate)).format(
                        'MM/DD/YYYY hh:mm A'
                      )
                    : ''}
                </td>
              </tr>
            </tbody>
          </table>
        </Col>
        {reportDueDateBlock ? (
          <Col className="" xs="12" sm="6" lg="2">
            {reportDueDateBlock}
          </Col>
        ) : null}
        <Col className="" xs="12" sm="6" lg="2">
          <table className="text-left">
            <tbody>
              <tr>
                <td>
                  <FontAwesomeIcon
                    icon={faUserMd}
                    className="color-secondary  mr-2  header-size-small "
                  />
                </td>
                <td>
                  <b>Professional</b>
                  <br />
                  {entity.appointment?.professionalName}
                </td>
              </tr>
            </tbody>
          </table>
        </Col>
        <SecureComponent permissions={[Permission.EditAppointment]}>
          <Col className="text-center" xs="12" sm="6" lg="2">
            <PrimaryButton onClick={redirectToAddedit}>Re-Assign</PrimaryButton>
          </Col>
        </SecureComponent>
      </Fragment>
    );
  } else {
    appointmentDateBlock = (
      <SecureComponent permissions={[Permission.EditAppointment]}>
        <Col className="text-center" xs="12" sm="6" lg="3">
          <div className="text-wrap">
            Pending Assignment
            <PrimaryButton onClick={redirectToAddedit} className="ml-3">
              Assign
            </PrimaryButton>
          </div>
        </Col>
      </SecureComponent>
    );
  }
  return (
    <>
      {userdata?.isClient ? (
        <AdminSubHeaderMainEditLayoutV3_1
          subRoute={ROUTES.REFERRALV3}
          title={entity?.stradixNumber ? `#${entity?.stradixNumber}   ` : ''}
          status={entity?.appointment?.appointmentStatus?.displayName}
          route={addEditRoute(AddEditSubRoute.Referral, entity.entityId)}
          editPermissions={[Permission.EditReferral]}
          lob={entity?.lineOfBusiness?.displayName}
          subservice={entity?.subService?.name}
          duedate={
            parseUtcToLocal(entity?.dueDate)
              ? dayjs(parseUtcToLocal(entity?.dueDate)).format('MM/DD/YYYY')
              : ''
          }
          claimantName={entity?.claimantName}
          claimantGender={entity?.claim?.claimant?.gender?.displayName}
          claimantDOB={
            parseRawDateString(entity?.claim?.claimant?.dateOfBirth)
              ? dayjs(parseRawDateString(entity?.claim?.claimant?.dateOfBirth)).format('M/DD/YYYY')
              : ''
          }
          dateOfInjury={
            parseRawDateString(entity?.claim?.dateOfInjury)
              ? dayjs(parseRawDateString(entity?.claim?.dateOfInjury)).format('M/DD/YYYY')
              : ''
          }
          dateAndTimeRequestReceived={
            parseUtcToLocal(entity?.dateAndTimeRequestReceived)
              ? dayjs(parseUtcToLocal(entity?.dateAndTimeRequestReceived)).format('M/DD/YYYY')
              : ''
          }
          dateOfDisability={
            parseRawDateString(entity?.claim?.dateOfDisability)
              ? dayjs(parseRawDateString(entity?.claim?.dateOfDisability)).format('M/DD/YYYY')
              : ''
          }
          claimNumber={entity?.claimNumber}
          referralNumber={entity?.referralNumber}
          showCaseMoreDetails={
            entity?.appointment?.appointmentStatus?.displayName ===
              'Assigned/Under-Review' ||
            entity?.appointment?.appointmentStatus?.displayName ===
              'Under Review' ||
            entity?.appointment?.appointmentStatus?.displayName === 'Closed' ||
            entity?.appointment?.appointmentStatus?.displayName === 'Cancelled'
          }
        />
      ) : (
        <>
          {/* Ownership Modal - only for internal users */}
          {isInternalUser && (
            <ReferralOwnershipModal
              isOpen={showModal}
              toggle={handleModalDismiss}
              entityId={id}
              hasExistingOwner={entity?.hasOwner || false}
              existingOwnerName={entity?.ownerName || ''}
              onOwnershipClaimed={handleOwnershipClaimed}
              onDeny={handleDeny}
              phase={entity?.currentPhase}
              checkpointId={entity?.ownershipCheckpointId}
            />
          )}

          {/* Ownership Banner - above breadcrumbs */}
          {showBanner && (
            <ReferralOwnershipBanner
              isOwner={bannerInfo.isOwner}
              ownerName={bannerInfo.ownerName}
            />
          )}

          <ViewReferralBreadcrumbs />
          <div>{appointmentStatusBar}</div>
          <PageCard noBottomMargin className="mb-small">
            <PageCardSection short centerContent className="bg-blue-accent ">
              <Row className="justify-content-center">
                <Col
                  className={`text-center${userdata?.isReviewer ? '' : ' table-responsive'}`}
                  xs="12"
                  sm="6"
                  lg="2"
                >
                  <table className="text-left">
                    <tbody>
                      <tr>
                        <td className="lg-1">
                          <FontAwesomeIcon
                            icon={faBuilding}
                            className="color-secondary  header-size-small mr-3"
                          />
                        </td>
                        <td className="lg-1">
                          <b>Client</b>
                          <br />
                          <div className="text-wrap">{entity.clientName}</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
                {referralDueDateBlock}
                {appointmentDateBlock}
              </Row>
            </PageCardSection>
          </PageCard>
          <AdminSubHeaderMainEditLayout
            subRoute={ROUTES.REFERRAL}
            title={
              (entity?.stradixNumber ? `#${entity?.stradixNumber} - ` : '') +
              (entity?.name ?? '') +
              (entity?.name == null ? '' : ' - ') +
              'Referral'
            }
            route={addEditRoute(AddEditSubRoute.Referral, entity.entityId)}
            editPermissions={[Permission.EditReferral]}
          />
        </>
      )}
    </>
  );
};
